import {
  isAllowedSocialUrl,
  sanitizeStorefrontNewsletterCollectionConfig,
  STOREFRONT_NEWSLETTER_CONSENT_VERSION,
  type WebsiteNewsletterCollection,
  type WebsiteInterfaceLocale,
  type WebsiteLinkValue,
  type WebsiteMediaValue,
  type WebsiteRegionFieldState,
  type WebsiteRichTextDocument,
  type WebsiteSlotSchema,
} from '@dukanti/shared';

/** The text a `text` slot shows by default, in the language of the store. */
export function defaultTextOf(
  slot: WebsiteSlotSchema,
  locale: WebsiteInterfaceLocale,
): string | null {
  return slot.defaults.kind === 'localized-text' ? slot.defaults[locale] : null;
}

/** True when "use the template again" means something for this slot. */
export function hasTemplateDefault(slot: WebsiteSlotSchema): boolean {
  return slot.defaults.kind !== 'none';
}

function authoredInner(state: WebsiteRegionFieldState): unknown {
  if (state.mode !== 'authored') return undefined;
  return 'v' in state.value ? state.value.v : undefined;
}

/** The text of an authored text field, or null. */
export function authoredString(state: WebsiteRegionFieldState): string | null {
  const inner = authoredInner(state);
  return typeof inner === 'string' ? inner : null;
}

/** Keep an unfinished method selection visible; the shared save schema rejects it. */
export function authoredNewsletterCollection(
  state: WebsiteRegionFieldState,
): WebsiteNewsletterCollection | null {
  const inner = authoredInner(state);
  if (inner === undefined) return null;
  const collection = sanitizeStorefrontNewsletterCollectionConfig(inner);
  if (collection) return collection;
  if (typeof inner !== 'object' || inner === null || Array.isArray(inner)) return null;
  const value = inner as Record<string, unknown>;
  if (
    Object.keys(value).length === 3 &&
    typeof value.enabled === 'boolean' &&
    Array.isArray(value.contactMethods) &&
    value.contactMethods.length === 0 &&
    value.consentVersion === STOREFRONT_NEWSLETTER_CONSENT_VERSION
  ) {
    return {
      enabled: value.enabled,
      contactMethods: [],
      consentVersion: STOREFRONT_NEWSLETTER_CONSENT_VERSION,
    };
  }
  return null;
}

export function authoredRichText(state: WebsiteRegionFieldState): WebsiteRichTextDocument | null {
  const inner = authoredInner(state);
  return typeof inner === 'object' && inner !== null && (inner as { type?: unknown }).type === 'doc'
    ? (inner as WebsiteRichTextDocument)
    : null;
}

function isLinkValue(value: unknown): value is WebsiteLinkValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { kind?: unknown }).kind === 'string'
  );
}

/** The link a link field shows: what the merchant set, else the template's, else none. */
export function currentLink(
  slot: WebsiteSlotSchema,
  state: WebsiteRegionFieldState,
): WebsiteLinkValue {
  const inner = authoredInner(state);
  if (isLinkValue(inner)) return inner;
  if (state.mode === 'default' && slot.defaults.kind === 'link') return slot.defaults.value;
  return { kind: 'none' };
}

/**
 * The media of an authored media field, as stored: the four keys and, since
 * epic #228 P6, its `framing`. Spreading it keeps the framing (PD6).
 */
export function authoredMedia(state: WebsiteRegionFieldState): WebsiteMediaValue | null {
  const inner = authoredInner(state);
  if (typeof inner !== 'object' || inner === null || !('mediaAssetId' in inner)) return null;
  return inner as WebsiteMediaValue;
}

/** The id an authored reference field points at, or ''. */
export function authoredRefId(state: WebsiteRegionFieldState): string {
  const inner = authoredInner(state);
  if (typeof inner !== 'object' || inner === null) return '';
  const record = inner as Record<string, unknown>;
  const id = record.categoryId ?? record.productId ?? record.collectionRef;
  return typeof id === 'string' ? id : '';
}

export function codePointLength(text: string): number {
  return Array.from(text).length;
}

/**
 * Epic #228 P5: the button label slot of a link slot (`<item>.ctaLink` →
 * `<item>.ctaLabel`), or null when the link is not a CTA link.
 */
export function ctaLabelSlotIdOf(linkSlotId: string): string | null {
  return linkSlotId.endsWith('.ctaLink') ? `${linkSlotId.slice(0, -'Link'.length)}Label` : null;
}

/**
 * True when a label field shows no words on the storefront: left empty,
 * authored blank, or "use the template" where the template has no words in
 * the store's language.
 */
export function labelResolvesEmpty(
  slot: WebsiteSlotSchema,
  state: WebsiteRegionFieldState,
  locale: WebsiteInterfaceLocale,
): boolean {
  if (state.mode === 'empty') return true;
  if (state.mode === 'default') return (defaultTextOf(slot, locale) ?? '').trim() === '';
  return (authoredString(state) ?? '').trim() === '';
}

/** What an unfinished destination is still missing. */
export type UnfinishedDestination = 'category' | 'product' | 'collection' | 'external';

/**
 * UI/UX P2-2: a link field holding a choice that cannot be saved yet: a
 * category/product/collection card with nothing picked, or an
 * Instagram/Facebook URL that is empty or refused. Null otherwise.
 */
export function unfinishedDestinationOf(
  slot: WebsiteSlotSchema,
  state: WebsiteRegionFieldState,
): UnfinishedDestination | null {
  if (slot.type !== 'link') return null;
  const link = currentLink(slot, state);
  if (link.kind === 'category' && link.categoryId === '') return 'category';
  if (link.kind === 'product' && link.productId === '') return 'product';
  if (link.kind === 'custom-collection' && link.collectionRef === '') return 'collection';
  if (link.kind === 'external' && !isAllowedSocialUrl(link.url)) return 'external';
  return null;
}
